import { useEffect, useState } from 'react';
import type { RapportKopf, RapportStatus } from '@arbeitsrapporte/shared';
import { RAPPORT_STATUS } from '@arbeitsrapporte/shared';
import { api } from '../api';
import { Fehler } from '../bausteine/Meldung';
import { Statusmarke } from '../bausteine/Statusmarke';
import { datumLesbar, STATUS_TEXT } from '../format';

/** Die Vorgabe des Filters – als Konstante, damit sie nicht bei jedem Rendern neu ist. */
const OFFENE: RapportStatus[] = ['entwurf', 'abgeschlossen'];
export const ZUR_FREIGABE: RapportStatus[] = ['abgeschlossen'];

/**
 * Filter und Suche stehen in der Adresse (`?status=…&suche=…`).
 *
 * Wer aus der Liste in einen Rapport geht und zurückkommt, soll dieselbe
 * Liste sehen – nicht die Vorgabe. `replaceState`, nicht `pushState`: Jeder
 * Tastendruck in der Suche wäre sonst ein Eintrag im Verlauf.
 */
function ausAdresse(vorgabe: RapportStatus[]): { status: RapportStatus[]; suche: string } {
  const p = new URLSearchParams(window.location.search);
  const status = p.get('status');

  return {
    status:
      status === null
        ? vorgabe
        : status
            .split(',')
            .filter((s): s is RapportStatus => (RAPPORT_STATUS as readonly string[]).includes(s)),
    suche: p.get('suche') ?? '',
  };
}

function inAdresse(status: RapportStatus[], suche: string, vorgabe: RapportStatus[]): void {
  const p = new URLSearchParams();
  const wieVorgabe = status.length === vorgabe.length && vorgabe.every((s) => status.includes(s));

  if (!wieVorgabe) p.set('status', status.join(','));
  if (suche) p.set('suche', suche);

  const s = p.toString();
  const neu = window.location.pathname + (s ? `?${s}` : '');

  if (neu !== window.location.pathname + window.location.search) {
    window.history.replaceState(null, '', neu);
  }
}

/**
 * Die Liste – für den Techniker seine, für die Leitung alle oder die zur
 * Freigabe. Was zu sehen ist, entscheidet die API; hier steht nur der
 * Filter.
 */
export function Liste({
  titel,
  status: vorgabeStatus,
  alle = false,
}: {
  titel: string;
  status?: RapportStatus[];
  /** Fremde Rapporte darin – dann steht der Techniker an jeder Karte. */
  alle?: boolean;
}) {
  const vorgabe = vorgabeStatus ?? OFFENE;
  const [status, setStatus] = useState<RapportStatus[]>(() => ausAdresse(vorgabe).status);
  const [suche, setSuche] = useState(() => ausAdresse(vorgabe).suche);
  const [daten, setDaten] = useState<RapportKopf[] | null>(null);
  const [gesamt, setGesamt] = useState(0);
  const [fehler, setFehler] = useState<unknown>(null);

  useEffect(() => {
    const stand = ausAdresse(vorgabe);

    setStatus(stand.status);
    setSuche(stand.suche);
  }, [vorgabe]);

  useEffect(() => {
    inAdresse(status, suche, vorgabe);

    /* Eine langsamere, ältere Antwort darf eine neuere nicht überschreiben. */
    let veraltet = false;
    const t = setTimeout(() => {
      api
        .rapporte({ status: status.length > 0 ? status : undefined, suche: suche || undefined })
        .then((s) => {
          if (veraltet) return;

          setDaten(s.daten);
          setGesamt(s.gesamt);
        })
        .catch((f: unknown) => {
          if (!veraltet) setFehler(f);
        });
    }, 150);

    return () => {
      veraltet = true;
      clearTimeout(t);
    };
  }, [status, suche, vorgabe]);

  const umschalten = (s: RapportStatus) =>
    setStatus(status.includes(s) ? status.filter((x) => x !== s) : [...status, s]);

  return (
    <>
      <h1 className="mb-4 text-2xl font-semibold">{titel}</h1>

      <div className="mb-4 space-y-3">
        <input
          className="feld"
          placeholder="Suchen: Auftrag, Kunde, Tätigkeit"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
        />
        {/*
          Die Filter als Schalter und nicht als Kästchen: Sie stehen zu
          fünft nebeneinander, und was an ist, soll man auf einen Blick
          sehen – nicht fünf Häkchen lesen müssen.
        */}
        <div className="flex flex-wrap gap-2">
          {RAPPORT_STATUS.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={status.includes(s)}
              onClick={() => umschalten(s)}
              className={`min-h-0 rounded-full border px-3 py-1.5 text-sm font-medium ${
                status.includes(s)
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              {STATUS_TEXT[s]}
            </button>
          ))}
        </div>
      </div>

      <Fehler fehler={fehler} />

      {daten === null && !fehler && <p className="text-slate-600">Lädt …</p>}
      {daten?.length === 0 && <p className="text-slate-600">Nichts gefunden.</p>}

      <ul className="space-y-2">
        {daten?.map((r) => (
          <li key={r.id}>
            <a href={`/rapport/${r.id}`} className="karte block hover:border-slate-400">
              <div className="flex items-center gap-3">
                <span className="font-semibold text-slate-900">{r.nummer}</span>
                <span className="text-sm text-slate-500">{datumLesbar(r.datum)}</span>
                <span className="ml-auto">
                  <Statusmarke status={r.status} />
                </span>
              </div>
              <div className="mt-1 text-slate-700">
                {r.kundeName || '–'} · {r.auftragNummer} {r.auftragBezeichnung}
              </div>
              {alle && <div className="mt-0.5 text-sm text-slate-500">{r.mitarbeiter.name}</div>}
              {r.taetigkeit && (
                <div className="mt-0.5 truncate text-sm text-slate-500">{r.taetigkeit}</div>
              )}
            </a>
          </li>
        ))}
      </ul>

      {/*
        Die Liste holt höchstens 200. Wer mehr hat, soll es sehen und die
        Suche benutzen – nicht glauben, der Rest sei nicht da.
      */}
      {daten && gesamt > daten.length && (
        <p className="mt-3 text-sm text-slate-500">
          {daten.length} von {gesamt} – die Suche grenzt weiter ein.
        </p>
      )}
    </>
  );
}
